import React, { useEffect, useState } from 'react';
import './ManualFileUploadPopup.scss';
import Rodal from '../../../../../components/Rodal/Rodal';
import { ModalBody, ModalHeader } from 'reactstrap';
import { ManualFileUploadForm } from './ManualFileUploadForm/ManualFileUploadForm';
import { DocumentSection } from '../../../../../slices/marketplace/timeline/models/document-section.model';
import { DocumentDetails } from '../../../../../slices/marketplace/timeline/models/document-details.model';
import SectionEditorModal from '../../../../AdminMarketplace/SectionEditorModal/SectionEditorModal';
import createNotification from '../../../../../utils/createNotification';
import { useStateSelector } from '../../../../../store/selectors';
import { useDispatch } from 'react-redux';
import { GetTimelineById } from '../../../../../slices/marketplace/timeline/thunks/get-timeline-by-id.thunk';
import ConfirmationModal from '../../../../../components/Modal/ConfirmationModal';
import { DocumentBuyerVisibilityConditions } from '../../../../../slices/marketplace/timeline/models/enums/engagement-document-party-buyer-visibility-conditions';
import { DocumentDetailsStatus } from '../../../../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import { ActionDocumentType } from '../../../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import DocumentsHistoryPopup from '../../../../AdminMarketplace/DocumetsHistoryPopup/DocumetsHistoryPopup';

interface ManualFileUploadPopupProps {
    engagementId: number;
    documentDetailsList: DocumentDetails[];
    documentsSections: DocumentSection[];
    isVisible: boolean;
    setIsVisible(value: boolean): void;
    sectionsModalMode: 'built-in' | 'external-modal';
    documentId?: string | undefined;
}

const ManualFileUploadPopup = (props: ManualFileUploadPopupProps) => {
    const dispatch = useDispatch() as any;
    const axios = useStateSelector((state) => state.core.axios);
    const [isUploadingDocument, setIsUploadingDocument] = useState(false);
    const [isSectionEditorModalVisible, setIsSectionEditorModalVisible] =
        useState(false);
    const [isSavingSections, setIsSavingSections] = useState(false);
    const [documentSectionList, setDocumentSectionList] = useState<
        DocumentSection[]
    >([]);
    const [documentToDelete, setDocumentToDelete] = useState<string>();
    const [isDeletingDocument, setIsDeletingDocument] = useState(false);

    const [isDocumentsHistoryPopupVisible, setIsDocumentsHistoryPopupVisible] =
        useState(false);

    const document = props.documentId
        ? props.documentDetailsList.find((doc) => doc.id === props.documentId)
        : undefined;

    useEffect(() => {
        setDocumentSectionList(props.documentsSections);
    }, [props.documentsSections]);

    const handleSave = (newDoc: DocumentDetails) => {
        setIsUploadingDocument(true);
        if (
            newDoc.buyerVisibilityConditions.includes(
                DocumentBuyerVisibilityConditions.FileApprovedBySeller
            )
        ) {
            newDoc.type = ActionDocumentType.DocumentWithFeedback;
            if (
                !document?.buyerVisibilityConditions?.includes(
                    DocumentBuyerVisibilityConditions.FileApprovedBySeller
                )
            ) {
                newDoc.status = DocumentDetailsStatus.RequiresAction;
            }
        } else {
            newDoc.type = ActionDocumentType.Downloadable;
            newDoc.status = DocumentDetailsStatus.Viewable;
        }
        axios
            .post(
                `/api/Marketplace/Engagements/${props.engagementId}/documents`,
                newDoc
            )
            .then((response: any) => {
                if (response && response.status === 200) {
                    createNotification(
                        'Document successfully uploaded.',
                        'success'
                    );
                    dispatch(
                        GetTimelineById({
                            id: props.engagementId,
                            isTemplate: false,
                        })
                    );
                } else {
                    const message = response.response.data.title;
                    createNotification(message || '', 'error');
                }
            })
            .catch((error) => {
                createNotification(error.message, 'error');
            })
            .finally(() => {
                setIsUploadingDocument(false);
                props.setIsVisible(false);
            });
    };

    const saveSections = () => {
        setIsSavingSections(true);
        axios
            .put(
                `api/marketplace/engagements/${props.engagementId}/documents/sections`,
                documentSectionList
            )
            .then((response) => {
                if (response && response.status === 200) {
                    createNotification(
                        'Document sections successfully saved.',
                        'success'
                    );
                    setIsSectionEditorModalVisible(false);
                    dispatch(
                        GetTimelineById({
                            id: props.engagementId,
                            isTemplate: false,
                        })
                    );
                } else {
                    createNotification(
                        'Some unexpected error occurred while saving document sections.',
                        'error'
                    );
                }
            })
            .finally(() => setIsSavingSections(false));
    };

    const deleteDocument = (documentId: string) => {
        setIsDeletingDocument(true);
        axios
            .delete(
                `/api/Marketplace/Engagements/${props.engagementId}/documents/${documentId}`
            )
            .then((response) => {
                if (response && response.status === 200) {
                    createNotification(
                        'Document has been successfully deleted.',
                        'success'
                    );
                    dispatch(
                        GetTimelineById({
                            id: props.engagementId,
                            isTemplate: false,
                        })
                    );
                    setDocumentToDelete(undefined);
                    props.setIsVisible(false);
                } else {
                    createNotification(
                        'Some unexpected error occurred while deleting document.',
                        'error'
                    );
                }
            })
            .catch((error) => {
                createNotification(error.message, 'error');
            })
            .finally(() => {
                setIsDeletingDocument(false);
            });
    };

    const getOldDocumentDetails = () => {
        let docDetailsList: DocumentDetails[] = [];

        if (
            !!document.oldManualReferenceIds &&
            document.oldManualReferenceIds?.length > 0
        ) {
            docDetailsList = props.documentDetailsList.filter(
                (d) =>
                    d.isManualDocument &&
                    (d.id == document.id ||
                        document.oldManualReferenceIds.includes(d.id))
            );
        }

        return docDetailsList;
    };

    const downloadFile = (documentId?: string) => {
        let doc: DocumentDetails | null = null;

        if (!!documentId) {
            doc = props.documentDetailsList.find((d) => d.id == documentId);
            if (!doc) return;
        } else if (!!document) {
            doc = document;
        } else {
            return;
        }

        const urlToFetchLink =
            doc.isUnregisteredFile && doc.temporaryFileId
                ? `api/TemporaryFiles/${doc.temporaryFileId}`
                : `/api/marketplace/engagements/${props.engagementId}/documentsDetails/${doc.id}`;
        axios
            .get(urlToFetchLink)
            .then((response: any) => {
                window.open(response.data, '_blank');
            })
            .catch(() =>
                createNotification(
                    'Some unexpected error occured while downloading file.',
                    'error'
                )
            );
    };

    return (
        <div className="manual-file-upload-popup-container">
            <Rodal
                visible={props.isVisible}
                onClose={() => props.setIsVisible(false)}
                animation="slideUp"
                className="manual-file-upload-popup"
                showMask={true}
                width={650}
                showCloseButton={true}>
                <ModalHeader>
                    <b>Manual File Upload</b>
                </ModalHeader>
                <ModalBody>
                    <div>
                        <ManualFileUploadForm
                            document={document}
                            onSubmit={handleSave}
                            onClose={() => {
                                props.setIsVisible(false);
                            }}
                            isVisible={props.isVisible}
                            documentsSections={documentSectionList}
                            onSectionsButtonClick={() =>
                                setIsSectionEditorModalVisible(true)
                            }
                            isSaving={isUploadingDocument}
                            setIsDocumentsHistoryPopupVisible={(isVisible) => {
                                setIsDocumentsHistoryPopupVisible(isVisible);
                            }}
                            downloadFile={downloadFile}
                            deleteDocument={(documentId: string) => {
                                setDocumentToDelete(documentId);
                            }}
                        />
                    </div>
                </ModalBody>
            </Rodal>
            {isSectionEditorModalVisible && (
                <SectionEditorModal
                    documentsSections={documentSectionList}
                    isVisible={isSectionEditorModalVisible}
                    onClose={() => {
                        setIsSectionEditorModalVisible(false);
                        setDocumentSectionList(props.documentsSections);
                    }}
                    setDocumentSectionList={setDocumentSectionList}
                    documentDetailsList={props.documentDetailsList}
                    sectionsModalMode={props.sectionsModalMode}
                    onSubmit={saveSections}
                    isSaving={isSavingSections}
                />
            )}
            {isDocumentsHistoryPopupVisible && (
                <DocumentsHistoryPopup
                    downloadDocument={downloadFile}
                    oldDocumentDetailsList={getOldDocumentDetails()}
                    isVisible={isDocumentsHistoryPopupVisible}
                    onClose={() => setIsDocumentsHistoryPopupVisible(false)}
                />
            )}
            {documentToDelete && (
                <ConfirmationModal
                    isVisible={true}
                    setIsVisible={() => setDocumentToDelete(undefined)}
                    header={'Are you sure?'}
                    confirmationText={
                        'Are you sure you want to delete this document?'
                    }
                    onConfirm={() => deleteDocument(documentToDelete)}
                    params={undefined}
                    nextButtonText="Delete"
                    cancelButtonText="Cancel"
                    closeOnConfirm={false}
                    isLoading={isDeletingDocument}
                />
            )}
        </div>
    );
};

export default ManualFileUploadPopup;
